import { useEffect, useRef, useState } from "react";
import { Send, Plus, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useTothChat } from "@/components/toth/useTothChat";
import { ChatMessage } from "@/components/toth/ChatMessage";
import { fmtDT } from "@/lib/format";
import { cn } from "@/lib/utils";

const SUGGEST = ["Com'è la situazione operativa oggi?", "Quali ordini sono scaduti e perché?", "Ci sono mezzi guasti o con manutenzione scaduta?", "Cosa scade nei prossimi 7 giorni?"];

const SessionList = ({ c }) => (
  <div className="space-y-1" data-testid="toth-session-list">
    <Button onClick={() => c.open(null)} variant="outline" className="mb-2 w-full justify-start rounded-xl" data-testid="toth-new-session"><Plus className="mr-1 h-4 w-4" />Nuova conversazione</Button>
    {c.sessions.map((s) => (
      <div key={s.id} className={cn("group flex items-center rounded-xl", s.id === c.sid ? "bg-accent" : "hover:bg-muted")}>
        <button onClick={() => c.open(s.id)} className="min-w-0 flex-1 px-3 py-2 text-left" data-testid={`toth-session-${s.id}`}>
          <p className="truncate text-sm font-semibold">{s.title}</p><p className="text-[11px] text-muted-foreground">{fmtDT(s.updated_at)}</p>
        </button>
        <button onClick={() => c.archive(s.id)} className="p-2 text-muted-foreground opacity-60 hover:text-red-600 group-hover:opacity-100" aria-label="Archivia" data-testid={`toth-session-archive-${s.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
  </div>
);

const Composer = ({ c, contextPath, disabled }) => {
  const [text, setText] = useState("");
  const go = (t) => {
    c.send(t, contextPath);
    setText("");
  };
  return (
    <div className="border-t bg-white p-3">
      {c.messages.length === 0 && (
        <div className="mb-3 flex flex-wrap gap-2">{SUGGEST.map((s, i) => <button key={s} disabled={disabled || c.busy} onClick={() => go(s)} className="rounded-full border bg-white px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50" data-testid={`toth-suggest-${i}`}>{s}</button>)}</div>
      )}
      <div className="flex items-end gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} disabled={disabled} rows={2} maxLength={4000} placeholder={disabled ? "Chat non disponibile" : "Chiedi a Toth…"}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); go(text); } }} className="min-h-[48px] resize-none rounded-xl" data-testid="toth-chat-input" />
        <Button onClick={() => go(text)} disabled={disabled || c.busy || !text.trim()} className="h-12 w-12 shrink-0 rounded-xl" aria-label="Invia" data-testid="toth-chat-send"><Send className="h-5 w-5" /></Button>
      </div>
    </div>
  );
};

export const TothChat = ({ compact = false, contextPath = "" }) => {
  const c = useTothChat();
  const [status, setStatus] = useState(null);
  const end = useRef(null);
  useEffect(() => {
    api.get("/toth/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [c.messages]);
  const disabled = status && !status.ai_chat_available;
  return (
    <div className={cn("flex min-h-0 gap-4", compact ? "h-full flex-col" : "h-[calc(100vh-15rem)] min-h-[520px]")} data-testid="toth-chat">
      {!compact && <aside className="card-soft hidden w-64 shrink-0 overflow-y-auto p-3 md:block">{<SessionList c={c} />}</aside>}
      <div className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", !compact && "card-soft")}>
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <select value={c.sid || ""} onChange={(e) => c.open(e.target.value || null)} className={cn("h-9 min-w-0 flex-1 rounded-lg border bg-white px-2 text-sm", !compact && "md:hidden")} data-testid="toth-session-select">
            <option value="">Nuova conversazione</option>{c.sessions.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <span className={cn("shrink-0 text-[11px] text-muted-foreground", compact ? "hidden sm:inline" : "hidden sm:inline md:flex-1")} data-testid="toth-chat-model">Claude · {status?.model || "…"} · solo dati autorizzati</span>
        </div>
        {disabled && <div className="m-3 flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700" data-testid="toth-chat-unavailable"><Lock className="h-4 w-4" />{status.unavailable_reason}</div>}
        <div className="flex-1 space-y-4 overflow-y-auto bg-muted/30 p-3 sm:p-4" data-testid="toth-chat-messages">
          {c.messages.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Chiedimi lo stato di ordini, criticità, mezzi e scadenze. Posso preparare proposte, che verranno eseguite solo dopo approvazione.</p>}
          {c.messages.map((m, i) => <ChatMessage key={m.id || i} m={m} idx={i} streaming={c.busy && i === c.messages.length - 1} />)}
          <div ref={end} />
        </div>
        <Composer c={c} contextPath={contextPath} disabled={disabled} />
      </div>
    </div>
  );
};
